import { useEffect, useMemo, useState } from 'react'
import {
  collection,
  documentId,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore'
import {
  chooseFor,
  chooseSession,
  describeFreshness,
  whyNoneOurs,
  type ConfiguredSeries,
  type NoneOursReason,
  type Entry,
  type EventDoc,
  type SessionLike,
  type StreamSessionDoc,
} from '@racedash/shared'
import { db } from '../lib/firebase'
import { useAttachments } from '../events/api'
import { useSeries, vendorNamesFor } from '../events/series'

/**
 * Where a dashboard's data comes from.
 *
 * A dashboard used to name an event. It no longer does, and that is the whole
 * change: a layout is not about a weekend, and binding it to one meant either a
 * dashboard per event or editing the same dashboard every fortnight.
 *
 * What replaces it is the team's own entries. An organisation already says "we
 * are at this event, running these cars" in order to be captured at all, and
 * that statement is a complete description of what data it may care about. So
 * the universe of visible timing is derived from the entries, the dashboard
 * carries only a layout, and what is being looked at right now lives in the URL
 * where it can be pasted to somebody.
 */

export interface AttachedEvent {
  eventId: string
  name: string
  trackId?: string
  startsAt: Date | null
  endsAt: Date | null
  /** With each entry's channel resolved from the event's per-series map. */
  entries: (Entry & { channel?: string | null })[]
  /** The event's other configured series, with their channels and stream names. */
  otherSeries: ConfiguredSeries[]
}

export interface SessionSummary extends StreamSessionDoc, SessionLike {
  id: string
}

/**
 * The events this organisation has entered, most recent first.
 *
 * Both directions in time, unlike the catalogue browser: the default view is
 * "the last thing we did" as often as it is "the thing happening now".
 */
export function useAttachedEvents(orgId: string | undefined) {
  const { attachments, error: attachError } = useAttachments(orgId)
  const series = useSeries()
  const [events, setEvents] = useState<Map<string, EventDoc>>(new Map())
  const [error, setError] = useState<string | null>(null)

  const ids = useMemo(
    () => (attachments ?? []).map((a) => a.eventId).filter(Boolean).sort(),
    [attachments],
  )
  const idKey = ids.join(',')

  useEffect(() => {
    if (ids.length === 0) {
      setEvents(new Map())
      return
    }
    // Firestore allows thirty values in an `in` clause, and a team's season is
    // a few dozen events at most — so chunks rather than a listener per event,
    // which would be one connection each for data that changes yearly.
    const chunks: string[][] = []
    for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30))

    const found = new Map<string, EventDoc>()
    const unsubs = chunks.map((chunk) =>
      onSnapshot(
        query(collection(db, 'events'), where(documentId(), 'in', chunk)),
        (snap) => {
          for (const d of snap.docs) found.set(d.id, d.data() as EventDoc)
          setEvents(new Map(found))
        },
        (e) => setError(e.message),
      ),
    )
    return () => unsubs.forEach((u) => u())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey])

  const attached = useMemo<AttachedEvent[]>(() => {
    return (attachments ?? [])
      .map((a) => {
        const e = events.get(a.eventId)
        return {
          eventId: a.eventId,
          name: e?.name ?? 'Unnamed event',
          trackId: e?.trackId,
          startsAt: e?.startsAt?.toDate() ?? null,
          endsAt: e?.endsAt?.toDate() ?? null,
          // A series reference is resolved to the stream's own strings here,
          // which is exactly what the relevance filter already means by
          // seriesMatched: authoritative, exact, no guessing. An entry that
          // named a series by hand keeps whatever it had.
          entries: (a.entries ?? []).map((entry) => {
            const names = vendorNamesFor(series, entry.seriesId)
            // The administrator's channel for this entry's series. Without one
            // the entry claims nothing.
            const channel =
              e?.source?.vendor === 'ris' && entry.seriesId
                ? (e.source.seriesChannels?.[entry.seriesId] ?? null)
                : null
            return { ...entry, ...(names ? { seriesMatched: names } : {}), channel }
          }),
          otherSeries: otherSeriesOf(e, a.entries ?? [], series),
        }
      })
      .sort((a, b) => (b.startsAt?.getTime() ?? 0) - (a.startsAt?.getTime() ?? 0))
  }, [attachments, events, series])

  return {
    events: attachments === null ? null : attached,
    error: attachError ?? error,
  }
}

/**
 * How long after an event's last day it still counts as "the current one".
 *
 * A race that ran until midnight is still what you want to see on Sunday
 * morning, and the catalogue's end date is a date rather than a moment.
 */
const stillCurrentMs = 18 * 60 * 60 * 1000

/**
 * Which event to open on, from dates alone.
 *
 * Deliberately decided without looking at any timing. If it needed session data
 * to choose, an event that has been entered but has not started would resolve
 * to nothing and silently fall back to last month's race — and "your weekend
 * has not started yet" is a thing worth saying rather than papering over.
 */
export function currentEvent(events: readonly AttachedEvent[], now: Date): AttachedEvent | null {
  if (events.length === 0) return null
  const nowMs = now.getTime()

  const running = events.filter(
    (e) =>
      (e.startsAt?.getTime() ?? Infinity) <= nowMs &&
      nowMs <= (e.endsAt?.getTime() ?? 0) + stillCurrentMs,
  )
  // Sorted newest-first already, so the first running one is the latest to have
  // started — which is the right answer when two overlap.
  if (running.length > 0) return running[0] ?? null

  const past = events.filter((e) => (e.endsAt?.getTime() ?? 0) + stillCurrentMs < nowMs)
  if (past.length > 0) return past[0] ?? null

  // Only future events left. The soonest, so the empty state can count down to
  // the right one.
  return events[events.length - 1] ?? null
}

/** Every timing bucket captured for one event. */
export function useEventSessions(eventId: string | null) {
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!eventId) {
      setSessions([])
      setLoading(false)
      return
    }
    setLoading(true)
    // Cleared on every resubscribe. A refused listener is not retried, so the
    // only way back from a refusal is a fresh subscription — and if the error
    // outlived it, the page would go on reporting a failure it had recovered
    // from. That is what a share link did while it waited for its identity.
    setError(null)
    return onSnapshot(
      query(
        collection(db, 'streamSessions'),
        where('eventId', '==', eventId),
        orderBy('lastSeenAt', 'desc'),
        // A weekend of sessions, not a season. More than this and something has
        // gone wrong with bucketing rather than with the query.
        limit(50),
      ),
      (snap) => {
        setSessions(
          snap.docs.map((d) => {
            const v = d.data() as StreamSessionDoc
            return { id: d.id, ...v, lastSeenMs: v.lastSeenAt?.toMillis() ?? null }
          }),
        )
        setLoading(false)
      },
      // Reported, never folded into an empty list: "no sessions yet" and "the
      // query was refused" look identical from the outside otherwise, and that
      // has already cost an afternoon once.
      (e) => {
        setError(e.message)
        setLoading(false)
      },
    )
  }, [eventId])

  return { sessions, loading, error }
}

export interface Selection {
  eventId?: string | null
  sessionId?: string | null
  car?: string | null
}

export interface DashboardSourceState {
  /** Every event this organisation has entered, newest first. */
  events: AttachedEvent[] | null
  /** The one being looked at. */
  event: AttachedEvent | null
  /** Sessions at that event the team's entries claim. */
  ours: SessionSummary[]
  /** Everything else captured there — reachable, never shown by default. */
  others: SessionSummary[]
  /** The bucket the screens read. */
  session: SessionSummary | null
  /** Whether what is on screen is happening now, said out loud. */
  freshness: { live: boolean; label: string }
  /**
   * True when the team has entries here but none of them claims any session.
   *
   * Its own state rather than an empty list, because the two need opposite
   * responses: nothing captured yet means wait, and nothing matched means the
   * series names disagree and somebody should say which is theirs. Null when
   * there is nothing to explain; otherwise which of those it is — including
   * 'waiting', when the event's sessions so far are another series'.
   */
  unmatched: NoneOursReason | null
  /**
   * What the dashboard would show with no session picked: the live session of
   * ours, else the most recent.
   *
   * Offered as the session list's first entry, "Auto: <that session>", so that
   * following along and having chosen look different. They did not: the list
   * showed the session either way, and nobody looking at a screen could tell
   * whether it would move to the race when the race went live.
   */
  auto: SessionSummary | null
  loading: boolean
  error: string | null
}

/**
 * Resolves a dashboard to an event, a set of relevant sessions, and one to show.
 *
 * The selection is passed in rather than stored, because it belongs to the
 * person looking rather than to the dashboard: two people on one team can be
 * watching different sessions, and one of them changing it must not yank the
 * other's screen mid-race. It rides in the URL, so "look at this" is still a
 * link.
 */
export function useDashboardSource(
  orgId: string | undefined,
  selection: Selection,
  now: Date,
): DashboardSourceState {
  const { events, error: eventsError } = useAttachedEvents(orgId)

  const event = useMemo(() => {
    if (!events) return null
    if (selection.eventId) {
      return events.find((e) => e.eventId === selection.eventId) ?? currentEvent(events, now)
    }
    return currentEvent(events, now)
    // `now` ticks; re-deriving the default every tick would fight a manual
    // choice, so only the event list and the explicit selection matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, selection.eventId])

  const { sessions, loading, error } = useEventSessions(event?.eventId ?? null)

  return useMemo(() => {
    const entries = event?.entries ?? []
    // A pick that no longer resolves shows nothing, which is right for somebody
    // who just chose it from a list — the shared view makes the other call.
    const { ours, others, session: chosen } = chooseFor(entries, sessions, {
      nowMs: now.getTime(),
      preferCar: selection.car,
      otherSeries: event?.otherSeries ?? [],
      picked: selection.sessionId,
      unresolved: 'nothing',
    })

    return {
      events,
      event,
      ours,
      others,
      auto: chooseSession(ours, { nowMs: now.getTime(), preferCar: selection.car }),
      session: chosen,
      freshness: describeFreshness(chosen, now.getTime()),
      unmatched:
        entries.length > 0 && sessions.length > 0 && ours.length === 0
          ? whyNoneOurs(entries, sessions, event?.otherSeries ?? [])
          : null,
      loading,
      error: eventsError ?? error,
    }
  }, [events, event, sessions, selection.sessionId, selection.car, now, loading, error, eventsError])
}

/**
 * The event's configured series the team is not entered in, each with its
 * channel and the names the stream uses for it.
 */
function otherSeriesOf(
  event: { source?: { vendor: string; seriesChannels?: Record<string, string> } } | undefined,
  entries: readonly { seriesId?: string }[],
  series: Parameters<typeof vendorNamesFor>[0],
): ConfiguredSeries[] {
  if (event?.source?.vendor !== 'ris') return []
  const mine = new Set(entries.map((e) => e.seriesId).filter(Boolean))
  return Object.entries(event.source.seriesChannels ?? {})
    .filter(([id]) => !mine.has(id))
    .map(([id, channel]) => ({ channel, names: vendorNamesFor(series, id) ?? [] }))
    .filter((c) => c.names.length > 0)
}
